import { useState, useMemo } from 'react';
import { 
  GitCompare, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  MapPin,
  Calendar,
  DollarSign,
  Truck,
  Home,
  Filter,
  RefreshCw,
  Save,
  TrendingUp,
  TrendingDown,
  Zap
} from 'lucide-react';
import { useExpenses } from '../contexts/ExpenseContext';

interface MatchResult {
  expenseId: string;
  date: string;
  description: string;
  amount: number;
  category: string;
  wasOnTrip: boolean;
  tripInfo?: {
    company: string;
    origin: string;
    destination: string;
    startDate: string;
    endDate: string;
  };
  matchType: 'on_trip' | 'home' | 'deposit_match' | 'deposit_mismatch' | 'unknown';
  suggestedAction?: string;
}

interface TripRange {
  startDate: string;
  endDate: string;
  company: string;
  origin: string;
  destination: string;
  miles: number;
  amount: number;
}

// Estimate trip duration based on miles
const estimateTripDays = (miles: number): number => {
  if (miles <= 0) return 1;
  return Math.max(1, Math.ceil(miles / 500));
};

export default function TransactionMatcher() {
  const { expenses, updateExpense } = useExpenses();
  const [isProcessing, setIsProcessing] = useState(false);
  const [filterType, setFilterType] = useState<'all' | 'on_trip' | 'home' | 'review'>('all');
  const [showOnlyUnmatched, setShowOnlyUnmatched] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);

  // Build trip ranges from settlements
  const tripRanges = useMemo((): TripRange[] => {
    const settlements = expenses.filter(e => e.category === 'settlements');
    
    const ranges = settlements.map(s => {
      const miles = s.miles || Math.round((s.amount || 0) / 1.75);
      const daysOnRoad = estimateTripDays(miles);
      
      const endDate = new Date(s.date);
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - daysOnRoad + 1);
      
      return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: s.date,
        company: s.company || 'Unknown',
        origin: s.origin || '',
        destination: s.destination || '',
        miles,
        amount: s.amount || 0
      };
    });
    
    // Merge overlapping/adjacent ranges (gaps <= 7 days = still OTR)
    const sorted = ranges.sort((a, b) => a.startDate.localeCompare(b.startDate));
    const merged: TripRange[] = [];
    
    for (const range of sorted) {
      if (merged.length === 0) {
        merged.push({ ...range });
        continue;
      }
      
      const last = merged[merged.length - 1];
      const lastEnd = new Date(last.endDate);
      const thisStart = new Date(range.startDate);
      const daysBetween = Math.floor((thisStart.getTime() - lastEnd.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysBetween <= 7) {
        // Extend the range
        if (range.endDate > last.endDate) {
          last.endDate = range.endDate;
        }
        last.miles += range.miles;
        last.amount += range.amount;
        // Keep first origin, update destination
        if (range.destination) last.destination = range.destination;
      } else {
        merged.push({ ...range });
      }
    }
    
    return merged;
  }, [expenses]);

  // Check if a date falls within any trip range
  const findTripForDate = (dateStr: string): TripRange | null => {
    for (const range of tripRanges) {
      if (dateStr >= range.startDate && dateStr <= range.endDate) {
        return range;
      }
    }
    return null;
  };

  // Analyze all non-settlement expenses
  const matchResults = useMemo((): MatchResult[] => {
    const nonSettlements = expenses.filter(e => e.category !== 'settlements');
    
    return nonSettlements.map(exp => {
      const trip = findTripForDate(exp.date);
      const desc = (exp.description || '').toLowerCase();
      
      // Check if this looks like a deposit/income
      const isDeposit = desc.includes('deposit') || 
                       desc.includes('transfer from') ||
                       desc.includes('💰');
      
      let matchType: MatchResult['matchType'] = 'unknown';
      let suggestedAction: string | undefined;
      
      if (isDeposit) {
        // Try to match deposit to a settlement
        const matchingSettlement = expenses.find(s => 
          s.category === 'settlements' &&
          Math.abs((s.amount || 0) - (exp.amount || 0)) < 1 &&
          Math.abs(new Date(s.date).getTime() - new Date(exp.date).getTime()) < 7 * 24 * 60 * 60 * 1000
        );
        
        if (matchingSettlement) {
          matchType = 'deposit_match';
        } else {
          matchType = 'deposit_mismatch';
          suggestedAction = 'Verify this deposit matches a settlement';
        }
      } else if (trip) {
        matchType = 'on_trip';
        if (exp.category === 'miscellaneous' || exp.category === 'meals') {
          suggestedAction = 'Mark as business expense (on trip)';
        }
      } else {
        matchType = 'home';
        // Check if it's a business-looking expense while home
        const businessKeywords = /fuel|diesel|truck|dot|permit|scale|toll|insurance|maintenance/i;
        if (businessKeywords.test(desc)) {
          suggestedAction = 'Review - business expense while home?';
        }
      }
      
      return {
        expenseId: exp.id,
        date: exp.date,
        description: exp.description || '',
        amount: exp.amount || 0,
        category: exp.category,
        wasOnTrip: !!trip,
        tripInfo: trip ? {
          company: trip.company,
          origin: trip.origin,
          destination: trip.destination,
          startDate: trip.startDate,
          endDate: trip.endDate
        } : undefined,
        matchType,
        suggestedAction
      };
    }).sort((a, b) => b.date.localeCompare(a.date)); // Most recent first
  }, [expenses, tripRanges]);

  // Filter results
  const filteredResults = useMemo(() => {
    return matchResults.filter(r => {
      if (filterType === 'on_trip' && r.matchType !== 'on_trip') return false;
      if (filterType === 'home' && r.matchType !== 'home') return false;
      if (filterType === 'review' && !r.suggestedAction) return false;
      if (showOnlyUnmatched && !r.suggestedAction) return false;
      return true;
    });
  }, [matchResults, filterType, showOnlyUnmatched]);

  // Statistics
  const stats = useMemo(() => {
    const onTrip = matchResults.filter(r => r.matchType === 'on_trip');
    const atHome = matchResults.filter(r => r.matchType === 'home');
    const needsReview = matchResults.filter(r => r.suggestedAction);
    const depositMatches = matchResults.filter(r => r.matchType === 'deposit_match');
    const depositMismatches = matchResults.filter(r => r.matchType === 'deposit_mismatch');
    
    return {
      total: matchResults.length,
      onTripCount: onTrip.length,
      onTripAmount: onTrip.reduce((sum, r) => sum + r.amount, 0),
      atHomeCount: atHome.length,
      atHomeAmount: atHome.reduce((sum, r) => sum + r.amount, 0),
      needsReviewCount: needsReview.length,
      depositMatchCount: depositMatches.length,
      depositMismatchCount: depositMismatches.length,
      tripDays: tripRanges.reduce((sum, r) => {
        const start = new Date(r.startDate);
        const end = new Date(r.endDate);
        return sum + Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      }, 0)
    };
  }, [matchResults, tripRanges]);

  // Auto-update expenses that were on trips
  const handleAutoUpdate = async () => {
    setIsProcessing(true);
    let updated = 0;
    
    const toUpdate = matchResults.filter(r => 
      r.matchType === 'on_trip' && 
      r.suggestedAction
    );
    
    for (const result of toUpdate) {
      const expense = expenses.find(e => e.id === result.expenseId);
      if (expense) {
        try {
          // Update description to indicate it was on a trip
          const tripNote = result.tripInfo 
            ? ` [OTR: ${result.tripInfo.company}]`
            : ' [OTR]';
          
          if (!expense.description?.includes('[OTR')) {
            await updateExpense({
              ...expense,
              description: (expense.description || '') + tripNote
            });
            updated++;
          }
        } catch (e) {
          console.error('Failed to update expense:', e);
        }
      }
    }
    
    setUpdateCount(updated);
    setIsProcessing(false);
  };

  const getMatchTypeStyles = (type: MatchResult['matchType']) => {
    switch (type) {
      case 'on_trip': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50';
      case 'home': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'deposit_match': return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'deposit_mismatch': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };

  const getMatchTypeIcon = (type: MatchResult['matchType']) => {
    switch (type) {
      case 'on_trip': return <Truck className="w-4 h-4" />;
      case 'home': return <Home className="w-4 h-4" />;
      case 'deposit_match': return <CheckCircle2 className="w-4 h-4" />;
      case 'deposit_mismatch': return <AlertTriangle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getMatchTypeLabel = (type: MatchResult['matchType']) => {
    switch (type) {
      case 'on_trip': return 'On Trip';
      case 'home': return 'At Home';
      case 'deposit_match': return 'Deposit ✓';
      case 'deposit_mismatch': return 'Deposit ?';
      default: return 'Unknown';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <GitCompare className="w-8 h-8 text-purple-400" />
            Transaction Matcher
          </h1>
          <p className="text-shuttle-sky mt-1">
            Cross-reference bank transactions with trip dates
          </p>
        </div>
        
        <button
          onClick={handleAutoUpdate}
          disabled={isProcessing || stats.needsReviewCount === 0}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-400 hover:to-pink-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isProcessing ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Zap className="w-4 h-4" />
          )}
          Auto-Tag OTR Expenses
        </button>
      </div>

      {/* Update notification */}
      {updateCount > 0 && (
        <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-400" />
          <p className="text-green-200">
            Updated {updateCount} expense(s) with OTR tags!
          </p>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-purple-500/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 text-purple-400 mb-1">
            <GitCompare className="w-4 h-4" />
            <span className="text-xs font-medium">Total Matched</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        
        <div className="bg-emerald-500/20 rounded-xl p-4 border border-emerald-500/30">
          <div className="flex items-center gap-2 text-emerald-400 mb-1">
            <Truck className="w-4 h-4" />
            <span className="text-xs font-medium">On Trip</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.onTripCount}</p>
          <p className="text-xs text-emerald-400/70">${stats.onTripAmount.toLocaleString()}</p>
        </div>
        
        <div className="bg-blue-500/20 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 text-blue-400 mb-1">
            <Home className="w-4 h-4" />
            <span className="text-xs font-medium">At Home</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.atHomeCount}</p>
          <p className="text-xs text-blue-400/70">${stats.atHomeAmount.toLocaleString()}</p>
        </div>
        
        <div className="bg-yellow-500/20 rounded-xl p-4 border border-yellow-500/30">
          <div className="flex items-center gap-2 text-yellow-400 mb-1">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xs font-medium">Needs Review</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.needsReviewCount}</p>
        </div>
        
        <div className="bg-green-500/20 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 text-green-400 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-medium">OTR Days</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.tripDays}</p>
        </div>
      </div>

      {/* Trip Ranges Summary */}
      {tripRanges.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-400" />
            Trip Periods ({tripRanges.length} OTR periods)
          </h2>
          <div className="flex flex-wrap gap-2">
            {tripRanges.slice(0, 10).map((range, idx) => (
              <div
                key={idx}
                className="px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-sm"
              >
                <div className="text-cyan-400 font-medium">
                  {range.startDate} → {range.endDate}
                </div>
                <div className="text-white/60 text-xs">
                  {range.company} • {range.miles.toLocaleString()} mi
                </div>
              </div>
            ))}
            {tripRanges.length > 10 && (
              <div className="px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white/60 text-sm flex items-center">
                +{tripRanges.length - 10} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <span className="text-white/70 text-sm flex items-center gap-2">
          <Filter className="w-4 h-4" />
          Filter:
        </span>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as any)}
          className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500"
        >
          <option value="all">All Transactions ({stats.total})</option>
          <option value="on_trip">🚛 On Trip ({stats.onTripCount})</option>
          <option value="home">🏠 At Home ({stats.atHomeCount})</option>
          <option value="review">⚠️ Needs Review ({stats.needsReviewCount})</option>
        </select>
        
        <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showOnlyUnmatched}
            onChange={(e) => setShowOnlyUnmatched(e.target.checked)}
            className="rounded border-white/30 bg-white/10 text-purple-500 focus:ring-purple-500"
          />
          Show only items needing action
        </label>
        
        <span className="ml-auto text-white/50 text-sm">
          {filteredResults.length} transactions
        </span>
      </div>

      {/* Results List */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
        <div className="divide-y divide-white/10 max-h-[600px] overflow-y-auto">
          {filteredResults.length === 0 ? (
            <div className="p-8 text-center text-white/50">
              <GitCompare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No transactions match the current filter</p>
              <p className="text-sm mt-2">Import bank statements to see matches</p>
            </div>
          ) : (
            filteredResults.map((result) => {
              return (
                <div
                  key={result.expenseId}
                  className={`p-4 hover:bg-white/5 transition-colors ${
                    result.suggestedAction ? 'bg-yellow-500/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Match type badge */}
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-medium whitespace-nowrap ${getMatchTypeStyles(result.matchType)}`}>
                      {getMatchTypeIcon(result.matchType)}
                      {getMatchTypeLabel(result.matchType)}
                    </div>
                    
                    {/* Date */}
                    <div className="w-24 flex items-center gap-1 text-white/60 text-sm">
                      <Calendar className="w-3 h-3" />
                      {result.date}
                    </div>
                    
                    {/* Description and trip info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white truncate text-sm">{result.description}</p>
                      {result.tripInfo && (
                        <div className="flex items-center gap-2 mt-1 text-xs text-emerald-400/70">
                          <MapPin className="w-3 h-3" />
                          <span>{result.tripInfo.company}: {result.tripInfo.origin || '?'} → {result.tripInfo.destination || '?'}</span>
                        </div>
                      )}
                      {result.suggestedAction && (
                        <div className="flex items-center gap-2 mt-1 text-xs text-yellow-400">
                          <AlertTriangle className="w-3 h-3" />
                          <span>{result.suggestedAction}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Category */}
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-white/10 text-white/70 border border-white/20">
                      {result.category}
                    </span>
                    
                    {/* Amount */}
                    <div className="text-right font-mono font-bold text-red-400 w-24">
                      -${result.amount.toFixed(2)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-medium text-white mb-3">🔗 How Transaction Matching Works</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-white/70">
          <div>
            <h4 className="font-medium text-purple-400 mb-2">Cross-Reference Logic:</h4>
            <ul className="space-y-1">
              <li>• Compares transaction dates against trip periods</li>
              <li>• Trips estimated from settlements (miles ÷ 500/day)</li>
              <li>• Gaps ≤7 days between trips = still OTR</li>
              <li>• Matches deposits to settlement amounts (±$1, ±7 days)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-emerald-400 mb-2">Auto-Tagging:</h4>
            <ul className="space-y-1">
              <li>• <span className="text-emerald-400">🚛 On Trip</span> = Expense during OTR period</li>
              <li>• <span className="text-blue-400">🏠 At Home</span> = Expense during home time</li>
              <li>• <span className="text-green-400">✓ Deposit Match</span> = Matches a settlement</li>
              <li>• <span className="text-yellow-400">⚠️ Review</span> = Needs manual verification</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

